import { useState } from "react";
import { useNavigate } from "react-router-dom";
import type { TelegramUser } from "../types";
import { API_HOST } from "../constants";

interface TelegramLoginButtonProps {
  onLogin: (user: TelegramUser) => void;
  botUsername: string;
  user?: TelegramUser | null;
}

export const TelegramLoginButton = ({ onLogin, botUsername, user }: TelegramLoginButtonProps) => {
  const [loading, setLoading] = useState(false);
  const [dataProcessingConsent, setDataProcessingConsent] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const navigate = useNavigate();

  // Обработка авторизации через бота
  const handleBotAuth = async () => {
    if (user) {
      navigate("/profile");
      return;
    }

    // Проверяем обязательное согласие на обработку данных
    if (!dataProcessingConsent) {
      alert("Для продолжения необходимо дать согласие на обработку персональных данных.");
      return;
    }

    setLoading(true);
    try {
      // Генерируем токен через API
      // marketing_consent всегда true по умолчанию для возможности рассылки
      const response = await fetch(`${API_HOST}/auth/generate-token`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          data_processing_consent: dataProcessingConsent,
          marketing_consent: marketingConsent,
        }),
      });

      if (!response.ok) {
        throw new Error("Ошибка генерации токена");
      }

      const data = await response.json();
      const token = data.token;

      // Определяем платформу для правильного формата deep link
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                   (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      
      // Сохраняем токен в localStorage для проверки статуса
      localStorage.setItem("authToken", token);
      
      // Переходим на страницу ожидания авторизации
      navigate(`/telegram-auth?token=${token}`);
      
      // Для iOS используем tg:// протокол
      // Для остальных платформ используем https://t.me/
      if (isIOS) {
        const iosUrl = `tg://resolve?domain=${botUsername}&start=${token}`;
        const webUrl = `https://t.me/${botUsername}?start=${token}`;
        
        const link = document.createElement('a');
        link.href = iosUrl;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        
        setTimeout(() => {
          document.body.removeChild(link);
        }, 100);
        
        setTimeout(() => {
          window.open(webUrl, "_blank");
        }, 1500);
      } else {
        const botUrl = `https://t.me/${botUsername}?start=${token}`;
        window.open(botUrl, "_blank");
      }
    } catch (err) {
      console.error(err);
      alert("Не удалось начать авторизацию. Попробуйте позже.");
      setLoading(false);
    }
  };

  if (user) return null;

  return (
    <div className="flex flex-col items-center justify-center min-h-[70vh] bg-neutral-light p-4">
      <div className="max-w-md w-full bg-neutral-bg rounded-lg shadow-md p-6 text-center space-y-6">
        <h1 className="text-2xl font-heading font-semibold mb-2 text-neutral-text">Войдите, чтобы начать</h1>
        <p className="text-neutral-text/70 font-body text-sm">
          Выберите удобный способ входа. Ваши данные защищены.
        </p>

        <div className="mt-4 space-y-3 text-left">
          <div className="bg-neutral-light border border-neutral-border rounded-lg p-3 mb-2">
            <p className="text-xs text-neutral-text/60 font-body mb-1.5 font-medium uppercase tracking-wide">Передаваемые данные</p>
            <ul className="text-xs text-neutral-text/70 space-y-0.5 list-disc list-inside">
              <li>Имя и фамилия (если указаны)</li>
              <li>Имя пользователя и фото профиля</li>
            </ul>
          </div>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={dataProcessingConsent}
              onChange={(e) => setDataProcessingConsent(e.target.checked)}
              className="mt-1 w-4 h-4 text-primary border-neutral-border rounded focus:ring-primary"
              required
            />
            <span className="text-sm text-neutral-text font-body">
              Я даю согласие на обработку персональных данных, включая трансграничную
              передачу данных AI-провайдерам (США, Китай), в соответствии с{" "}
              <a href="/privacy" target="_blank" className="text-primary hover:underline">
                политикой конфиденциальности
              </a>
              {" "}*
            </span>
          </label>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={marketingConsent}
              onChange={(e) => setMarketingConsent(e.target.checked)}
              className="mt-1 w-4 h-4 text-primary border-neutral-border rounded focus:ring-primary"
            />
            <span className="text-sm text-neutral-text/70 font-body">
              Соглашаюсь получать новости об обновлениях платформы
            </span>
          </label>
        </div>

        <div className="flex flex-col gap-3 mt-6">
          <button
            onClick={handleBotAuth}
            disabled={loading || !dataProcessingConsent}
            className={`px-6 py-3 font-semibold rounded-lg shadow-md transition-colors ${
              !dataProcessingConsent
                ? "bg-gray-300 text-gray-500 cursor-not-allowed opacity-50"
                : "bg-blue-500 hover:bg-blue-600 text-white disabled:opacity-50 disabled:cursor-not-allowed"
            }`}
          >
              {loading ? "Загрузка..." : "Войти через Telegram"}
          </button>

          <button
            onClick={async () => {
              if (!dataProcessingConsent) {
                alert("Для продолжения необходимо дать согласие на обработку персональных данных.");
                return;
              }
              setLoading(true);
              try {
                const res = await fetch(
                  `${API_HOST}/auth/yandex/url?data_processing_consent=${dataProcessingConsent}&marketing_consent=${marketingConsent}`
                );
                if (!res.ok) throw new Error("Ошибка получения URL авторизации");
                const data = await res.json();
                window.location.href = data.url;
              } catch (err) {
                console.error(err);
                alert("Не удалось начать авторизацию через Яндекс. Попробуйте позже.");
                setLoading(false);
              }
            }}
            disabled={loading || !dataProcessingConsent}
            className={`px-6 py-3 font-semibold rounded-lg shadow-md transition-colors flex items-center justify-center gap-2 ${
              !dataProcessingConsent
                ? "bg-gray-300 text-gray-500 cursor-not-allowed opacity-50"
                : "bg-yellow-400 hover:bg-yellow-500 text-black disabled:opacity-50 disabled:cursor-not-allowed"
            }`}
          >
            <span className="text-base leading-none">Я</span>
            {loading ? "Загрузка..." : "Войти через Яндекс ID"}
          </button>
        </div>

        <p className="mt-4 text-neutral-text/60 text-xs font-body">
          При входе через Telegram откроется бот — нажмите «Запустить» или отправьте /start.
        </p>
      </div>
    </div>
  );
};
